import {DynamicParametersType} from '../dynamicParameters/dynamicParameters.enums';
import {TextJustification} from './aiContentProject.enums';

export enum AiContentVariationColumnDataType {
  Text = 0,
  Number = 1,
  Color = 2,
  Image = 3,
  Video = 4,
  Persona = 5,
  Language = 6,
}

/**
 * Natural language enum for LLM interaction.
 * Use getMatchingEnumValue to convert between this and AiContentVariationColumnDataType.
 */
export enum AiAiContentVariationColumnDataType {
  Text = 'Text',
  Number = 'Number',
  Color = 'Color',
  Image = 'Image',
  Video = 'Video',
  Persona = 'Persona',
  Language = 'Language',
}

export interface RemoteBriefTargetPlatform {
  platformId: number;
  platformName?: string | null;
}

export interface RemoteAiContentBrief {
  objective?: string | null;
  description?: string | null;
  targetPlatforms?: RemoteBriefTargetPlatform[] | null;
  personaIds?: number[] | null;
  languages?: string[] | null;
  applyBrandGuidelines: boolean;
}

export interface RemoteAiContentVariationColumn {
  name?: string | null;
  dataType: AiContentVariationColumnDataType;
  source?: string | null;
  isRequired: boolean;
  usedFor?: string | null;
}

export interface RemoteAiContentVariationInputSchema {
  columns?: RemoteAiContentVariationColumn[] | null;
}

/**
 * Focal rectangle (non-obscured / safe content area)
 */
export interface RemoteAiContentFocalRect {
  /**
   * X coordinate of the focal rectangle
   */
  x: number;

  /**
   * Y coordinate of the focal rectangle
   */
  y: number;

  /**
   * Width of the focal rectangle
   */
  width: number;

  /**
   * Height of the focal rectangle
   */
  height: number;
}

/**
 * Text-specific specifications
 */
export interface RemoteAiContentTextSpec {
  /**
   * Font size in pixels
   */
  fontSize: number;

  /**
   * Line height multiplier
   */
  lineHeight: number;

  /**
   * URL to the font file
   */
  fontFileUrl?: string | null;

  /**
   * Text box width in pixels
   */
  textBoxWidth: number;

  /**
   * Text box height in pixels
   */
  textBoxHeight: number;

  /**
   * Layer name in the template
   */
  layerName: string;

  /**
   * Text justification
   */
  justification?: TextJustification | null;
}

/**
 * Image-specific specifications
 */
export interface RemoteAiContentImageSpec {
  /**
   * Image width
   */
  width: number;

  /**
   * Image height
   */
  height: number;

  layerName: string;

  /**
   * Non-obscured / safe content area (focal rectangle)
   */
  focalRect?: RemoteAiContentFocalRect | null;
}

/**
 * Video-specific specifications
 */
export interface RemoteAiContentVideoSpec {
  width: number;
  height: number;
  duration: number;//Duration in seconds
  focalRect?: RemoteAiContentFocalRect | null;//Non-obscured / safe content area (focal rectangle)
}

/**
 * Dynamic parameter specifications for a specific ad size
 */
export interface RemoteAiContentContentFeedColumnParamsBySize {
  /**
   * Text-specific requirements for this param in this ad size
   */
  textSpec?: RemoteAiContentTextSpec | null;

  /**
   * Image-specific requirements for this param in this ad size
   */
  imageSpec?: RemoteAiContentImageSpec | null;

  /**
   * Video-specific requirements for this param in this ad size
   */
  videoSpec?: RemoteAiContentVideoSpec | null;
}

/**
 * Content feed column dynamic parameter specification
 */
export interface RemoteAiContentContentFeedColumnParams {
  /**
   * Content feed column name (taxonomy key) for Content source dynamic parameters (e.g. "headline", "hero_image")
   */
  contentFeedColumnName: string;

  /**
   * Clinch dynamic parameter type enum
   */
  type: DynamicParametersType;

  /**
   * Free text describing what this param is used for
   */
  purpose: string;

  /**
   * Per-ad-size specifications for this dynamic param
   * Key is ad size (e.g. "300x250"), value is the specs
   */
  specsByAdSize: Record<string, RemoteAiContentContentFeedColumnParamsBySize>;

  /**
   * AI content generation setup configuration
   */
  generationSetup?: RemoteAiContentGenerationSetup | null;
}

/**
 * Content feed columns with their specifications per ad size
 * Key is content feed column name (taxonomy key), value is the column specs
 */
export interface RemoteAiContentContentFeedColumnSpecs {
  /**
   * Content feed columns with their specifications per ad size
   * Key is content feed column name (taxonomy key), value is the column specs
   */
  columns: Record<string, RemoteAiContentContentFeedColumnParams>;
}

/**
 * Ad sizes (e.g. ["300x250", "728x90", "1080x1920"])
 */
export interface RemoteAiContentTemplateMetadata {
  adSizes: string[];

  /**
   * Content feed columns with their specifications per ad size
   * Key is content feed column name (taxonomy key), value is the column specs
   */
  contentFeedColumns: RemoteAiContentContentFeedColumnSpecs;
}

/**
 * Template summary containing basic template information
 */
export interface RemoteTemplateSummary {
  id: number;
  name?: string | null;
  type?: number | null;
  posterUrl?: string | null;
  features?: string[] | null;
}

export interface RemoteAiContentProject {
  id?: number;
  name?: string | null;
  brief?: RemoteAiContentBrief | null;
  templateId?: number | null;
  templateSummary?: RemoteTemplateSummary | null;
  templateMetadata?: RemoteAiContentTemplateMetadata | null;
  variationInputSchema?: RemoteAiContentVariationInputSchema | null;
}

export interface CreateAiContentProjectRequest {
  name?: string | null;
  brief?: RemoteAiContentBrief | null;
  templateId?: number | null;
  variationInputSchema?: RemoteAiContentVariationInputSchema | null;
}

export interface UpdateAiContentProjectRequest {
  name?: string | null;
  brief?: RemoteAiContentBrief | null;
  templateId?: number | null;
  variationInputSchema?: RemoteAiContentVariationInputSchema | null;
}

/**
 * Used input column for AI content generation
 */
export interface RemoteAiContentUsedInputColumn {
  columnName: string;
}

/**
 * Image smart crop setup configuration
 */
export interface RemoteAiContentImageSmartCropSetup {
  inputColumnName?: string | null;
  objectDetectPrompt?: string | null;
  marginPercent: number;
}

/**
 * Image AI generation setup configuration
 */
export interface RemoteAiContentImageAiGenerationSetup {
  usedInputColumns?: RemoteAiContentUsedInputColumn[] | null;
  vendorService?: number | null;
  vendorServiceWithReferences?: number | null;
  useSharedIntermediateImage?: boolean; // When true, uses shared intermediate image flow
}

/**
 * Text generation setup configuration
 */
export interface RemoteAiContentTextGenerationSetup {
  usedInputColumns?: RemoteAiContentUsedInputColumn[] | null;
}

/**
 * Product shot generation setup configuration
 */
export interface RemoteAiContentProductShotSetup {
  productImageInputColumnName?: string | null;
  ScenePromptInputColumnName?: RemoteAiContentUsedInputColumn[] | null;
  OptionalSceneReferenceImageInputColumnName?: string | null;
  marginPercent: number;
  productType: number;
  useSharedIntermediateImage?: boolean; // When true, uses shared intermediate image flow
}

/**
 * AI content generation setup configuration
 */
export interface RemoteAiContentGenerationSetup {
  imageSmartCrop?: RemoteAiContentImageSmartCropSetup | null;
  imageAiGeneration?: RemoteAiContentImageAiGenerationSetup | null;
  productShotGeneration?: RemoteAiContentProductShotSetup | null;
  textGeneration?: RemoteAiContentTextGenerationSetup | null;
}

/**
 * Request payload for updating generation setup
 */
export interface UpdateGenerationSetupRequest {
  contentFeedColumnName?: string | null;
  generationSetup: RemoteAiContentGenerationSetup;
}

